/**
 * Order Generation Service
 * Generates printable order forms with cut sheets and bill of materials
 */

import QRCode from 'qrcode';
import type { OrderFormData, CalculationResult } from '../types/madebb.types';
import { escapeHtml } from '../utils/htmlUtils';

export class OrderGenerationService {
  /**
   * Map material name and color to inventory fabric ID
   */
  private static getFabricInventoryId(material: string, colour: string): string {
    const materialMap: { [key: string]: string } = {
      'sunbrella': 'SUNB',
      'pfiphertex': 'PFIP',
      'weblon': 'WEBL'
    };

    const colourMap: { [key: string]: string } = {
      'black': 'BLK',
      'toast': 'TOA'
    };

    const materialCode = materialMap[material.toLowerCase()] || 'SUNB';
    const colourCode = colourMap[colour.toLowerCase()] || 'BLK';

    return `FAB-${materialCode}-${colourCode}`;
  }

  /**
   * Generate and open printable order form
   */
  static async generateOrderForm(
    formData: OrderFormData,
    calculations: CalculationResult,
    orderNumber: number,
    securityToken: string
  ): Promise<void> {
    const orderId = `MB-${orderNumber.toString().padStart(3, '0')}`;
    const orderData = {
      ...formData,
      orderId,
      orderNumber,
      orderDate: new Date().toLocaleDateString(),
      materialName: this.capitalize(formData.material),
      colourName: this.capitalize(formData.colour),
      hardwareColourName: this.capitalize(formData.hardwareColour)
    };

    // Generate QR code for production floor status updates (with security token)
    const statusUpdateUrl = `${window.location.origin}/madebb/status/${orderId}?token=${securityToken}`;
    const qrCodeDataUrl = await QRCode.toDataURL(statusUpdateUrl, {
      width: 150,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });

    const htmlContent = this.generateHTMLOrderForm(orderData, calculations, qrCodeDataUrl);

    // Open in new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
    }
  }

  /**
   * Generate complete HTML order form
   */
  private static generateHTMLOrderForm(orderData: any, costs: CalculationResult, qrCodeDataUrl: string): string {
    const pineCutLength = orderData.width - 1;
    const bottomPipeCutLength = orderData.width - 1;
    // All units need roll pulleys - width only determines count (2 or 3)
    const needsRollPulleys = true;

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Made.bb Roll Shades Order - ${orderData.orderId}</title>
    <style>
        @page {
            size: landscape;
            margin: 0.5in;
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.3;
            color: #000;
            background: white;
        }

        .page {
            width: 100%;
            page-break-after: always;
            padding: 14px;
        }

        .page-cut-sheet {
            height: 7.5in;
            max-height: 7.5in;
            page-break-inside: avoid;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        }

        .page:last-child {
            page-break-after: avoid;
        }

        .column-container {
            display: flex;
            gap: 16px;
            width: 100%;
        }

        .page-cut-sheet .column-container {
            flex: 1;
            min-height: 0;
            overflow: hidden;
        }

        .left-column {
            flex: 0 0 38%;
            width: 38%;
        }

        .right-column {
            flex: 0 0 62%;
            width: 62%;
        }

        .page-cut-sheet .right-column {
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }

        .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 7px;
        }

        .header h1 {
            font-size: 20px;
            margin-bottom: 4px;
        }

        .header p {
            font-size: 14px;
            color: #666;
        }

        .order-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-weight: bold;
        }

        .table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }

        .table th,
        .table td {
            border: 1px solid #000;
            padding: 4px 6px;
            text-align: left;
        }

        .table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }

        .table tr.subsection-row td {
            background-color: #f9f9f9;
            font-size: 10px;
            font-style: italic;
            border-left: none;
        }

        .table tr.subsection-row td:first-child {
            border-left: 1px solid #000;
        }

        .section-title {
            font-size: 14px;
            font-weight: bold;
            margin: 10px 0 6px 0;
            color: #000;
        }

        .pattern-container {
            width: 100%;
            height: 300px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 4px 0;
            overflow: hidden;
        }

        .pattern-svg {
            border: 1px solid #ccc;
            width: 100%;
            max-width: 640px;
            height: auto;
            max-height: 300px;
        }
        .dim-witness { stroke: #999; stroke-width: 0.75; }
        .dim-line    { stroke: #555; stroke-width: 1; }
        .dim-text    { fill: #000; font: 12px Tahoma, sans-serif;
                       paint-order: stroke; stroke: #fff; stroke-width: 3px; }
        .leader-line { stroke-width: 1; }
        .leader-text { font: 12px Tahoma, sans-serif; font-weight: 600;
                       paint-order: stroke; stroke: #fff; stroke-width: 3px; }

        .notes {
            margin-top: 20px;
            font-size: 11px;
        }

        .notes ul {
            margin-left: 20px;
        }

        .highlight {
            background-color: #ffff99;
            padding: 2px;
        }

        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { padding: 10px; }
            .page-cut-sheet {
                height: 7.5in;
                max-height: 7.5in;
            }
            .pattern-container {
                height: 280px;
                max-height: 280px;
            }
            .pattern-svg {
                max-height: 280px;
            }
        }
    </style>
</head>
<body>
    ${this.generateCutSheetHTML(orderData, costs, pineCutLength, bottomPipeCutLength, qrCodeDataUrl)}
    ${this.generateBillOfMaterialsHTML(orderData, costs, needsRollPulleys)}
</body>
</html>`;
  }

  /**
   * Generate cut sheet page
   */
  private static generateCutSheetHTML(orderData: any, costs: CalculationResult, pineCutLength: number, bottomPipeCutLength: number, qrCodeDataUrl: string): string {
    const patternContent = this.generatePatternContent(orderData, costs);

    return `
<div class="page page-cut-sheet">
    <div class="header">
        <h1>Made.bb Roll Shades - Cut Sheet</h1>
    </div>

    <div class="order-info">
        <div>Order Number: ${orderData.orderId}</div>
        <div>Date: ${orderData.orderDate}</div>
        <div style="text-align: right;">
            <div style="display: inline-block; text-align: center;">
                <img src="${qrCodeDataUrl}" alt="Status Update QR Code" style="width: 80px; height: 80px; border: 2px solid #000;"/>
                <div style="font-size: 10px; margin-top: 4px;">Scan to Update Status</div>
            </div>
        </div>
    </div>

    <div class="column-container">
        <div class="left-column">
            <div class="section-title">SPECIFICATIONS</div>
            <table class="table">
                <thead>
                    <tr>
                        <th>Dimension</th>
                        <th>Material</th>
                        <th>Colour</th>
                        <th>Hardware Colour</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>${orderData.width}" W × ${orderData.drop}" D</td>
                        <td>${escapeHtml(orderData.materialName)}</td>
                        <td>${escapeHtml(orderData.colourName)}</td>
                        <td>${escapeHtml(orderData.hardwareColourName)}</td>
                    </tr>
                    <tr>
                        <th>Fabric Yardage</th>
                        <th>Production Units</th>
                        <th colspan="2">Order Details</th>
                    </tr>
                    <tr>
                        <td>${costs.fabricYardage} yards</td>
                        <td>${(costs.bulkRate * 0.01).toFixed(2)}</td>
                        <td colspan="2">Order: ${orderData.orderId}</td>
                    </tr>
                </tbody>
            </table>

            <div class="section-title">CUT ELEMENTS</div>
            <table class="table">
                <thead>
                    <tr>
                        <th>Component</th>
                        <th>Dimensions</th>
                    </tr>
                </thead>
                <tbody>
                    ${this.generateFabricCutRows(orderData)}
                    <tr>
                        <td>2x2 - Treated Pine</td>
                        <td>${pineCutLength}"</td>
                    </tr>
                    <tr>
                        <td>Bottom Pipe</td>
                        <td>${bottomPipeCutLength}"</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="right-column">
            ${patternContent}
        </div>
    </div>
</div>`;
  }

  /**
   * Generate pattern visualization — architectural dimensioning, no flare.
   */
  private static generatePatternContent(orderData: any, costs: CalculationResult): string {
    // Geometry (unchanged)
    const unitWidth = orderData.width;
    const topSectionWidth = unitWidth - 1;       // 1" narrower than unit width
    const bottomSectionWidth = unitWidth + 2;    // 1" seam allowance per side (double fold: 2 × 0.5")
    const topSectionHeight = 8;
    const bottomLength = orderData.drop + 3;
    const totalLength = topSectionHeight + bottomLength;
    const seamAllowance = 1.0;
    const bottomSeamPosition = 3;

    // Gutter-based canvas sizing
    const GUTTER_LEFT = 70, GUTTER_RIGHT = 160, GUTTER_TOP = 45, GUTTER_BOTTOM = 55;
    const drawBoxW = 320, drawBoxH = 300;
    const svgWidth  = GUTTER_LEFT + drawBoxW + GUTTER_RIGHT;   // 550
    const svgHeight = GUTTER_TOP  + drawBoxH + GUTTER_BOTTOM;  // 400

    // Scale: fit bottom section (widest) and total length into draw box
    const maxScaleWidth  = drawBoxW / bottomSectionWidth;
    const maxScaleHeight = drawBoxH / totalLength;
    const scale = Math.min(maxScaleWidth, maxScaleHeight) * 0.85;

    // Draw dimensions
    const topDrawWidth         = topSectionWidth * scale;
    const bottomDrawWidth      = bottomSectionWidth * scale;
    const topSectionDrawHeight = topSectionHeight * scale;
    const bottomDrawLength     = bottomLength * scale;
    const totalDrawLength      = topSectionDrawHeight + bottomDrawLength;
    const seamAllowanceDraw    = seamAllowance * scale;
    const bottomSeamDraw       = bottomSeamPosition * scale;

    // Drawing positions
    const patternStartY = GUTTER_TOP;
    const bottomStartX  = GUTTER_LEFT + (drawBoxW - bottomDrawWidth) / 2;
    const topStartX     = bottomStartX + (bottomDrawWidth - topDrawWidth) / 2;
    const drawLeftEdge  = GUTTER_LEFT;
    const drawRightEdge = GUTTER_LEFT + drawBoxW;
    const seamLineY     = patternStartY + topSectionDrawHeight;
    const bottomEdgeY   = patternStartY + totalDrawLength;
    const bottomSeamY   = bottomEdgeY - bottomSeamDraw;

    // Scale-aware component sizing — proportional to topDrawWidth
    const maxComponentWidth = topDrawWidth * 0.35;  // 35% of top rect for nameplate
    const namePlateWidth  = Math.min(4 * scale, maxComponentWidth);
    const namePlateHeight = Math.min(1 * scale, namePlateWidth * 0.25);

    const namePlateRightX = topStartX + topDrawWidth - Math.max(2 * scale, topDrawWidth * 0.05);
    const namePlateLeftX  = Math.max(topStartX + 2, namePlateRightX - namePlateWidth);
    const namePlateY      = seamLineY - namePlateHeight - (0.5 * scale);

    // Roll pulleys (proportional sizing)
    const rollPulleyCount = unitWidth > 48 ? 3 : 2;
    let pulleyElements = '';
    const pulleyWidth  = Math.min(2 * scale, topDrawWidth * 0.12);
    const pulleyHeight = Math.min(1 * scale, pulleyWidth * 0.5);
    const pulleyY      = seamLineY - pulleyHeight - Math.max(0.5 * scale, 2);

    // Right pulley placement
    const firstPulleyRightX = Math.max(
      topStartX + pulleyWidth + 2,
      namePlateLeftX - Math.max(0.5 * scale, 3)
    );
    const firstPulleyLeftX = firstPulleyRightX - pulleyWidth;

    // Mirror left pulley symmetrically — clamp to rect bounds
    const rightPulleyDistanceFromEdge = (topStartX + topDrawWidth) - firstPulleyRightX;
    const leftPulleyLeftX = Math.min(
      topStartX + topDrawWidth - pulleyWidth - 2,
      topStartX + rightPulleyDistanceFromEdge
    );

    const pulleys: Array<{x: number, y: number, label: string}> = [];
    if (rollPulleyCount >= 2) {
      pulleys.push({ x: firstPulleyLeftX, y: pulleyY, label: 'R' });
      pulleys.push({ x: leftPulleyLeftX,  y: pulleyY, label: 'L' });
      if (rollPulleyCount === 3) {
        const middlePulleyX = leftPulleyLeftX + ((firstPulleyLeftX - leftPulleyLeftX) / 2);
        pulleys.push({ x: middlePulleyX, y: pulleyY, label: 'M' });
      }
    }

    pulleys.forEach(pulley => {
      const centerX = pulley.x + pulleyWidth / 2;
      const centerY = pulley.y + pulleyHeight / 2;
      pulleyElements += `
        <!-- Roll Pulley ${pulley.label} -->
        <rect x="${pulley.x.toFixed(2)}" y="${pulley.y.toFixed(2)}" width="${pulleyWidth.toFixed(2)}" height="${pulleyHeight.toFixed(2)}"
              fill="none" stroke="#000" stroke-width="1"/>
        <circle cx="${(centerX - pulleyWidth/4).toFixed(2)}" cy="${centerY.toFixed(2)}" r="1" fill="#000"/>
        <circle cx="${(centerX + pulleyWidth/4).toFixed(2)}" cy="${centerY.toFixed(2)}" r="1" fill="#000"/>`;
    });

    // SVG marker defs
    const svgDefs = `
  <defs>
    <marker id="dimArrow" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="7" markerHeight="7" orient="auto-start-reverse">
      <path d="M0,1 L9,5 L0,9" fill="none" stroke="#555" stroke-width="1.3"/>
    </marker>
    <marker id="leadDot" viewBox="0 0 6 6" refX="3" refY="3"
            markerWidth="6" markerHeight="6">
      <circle cx="3" cy="3" r="2" fill="#FF6600"/>
    </marker>
  </defs>`;

    // Fabric join seam lines + callout data
    const seamResult = this.generateSeamLines(
      unitWidth,
      topStartX, topDrawWidth,
      bottomStartX, bottomDrawWidth,
      patternStartY, topSectionDrawHeight,
      seamLineY, bottomDrawLength,
      scale
    );

    // Right-gutter callout allocator — sort by feature Y, assign non-overlapping slots
    const textX = drawRightEdge + 24;
    const callouts: Array<{fx: number, fy: number, label: string, color: string}> = [
      { fx: topStartX + topDrawWidth,                          fy: seamLineY,                             label: '8" SEAM',          color: '#0000FF' },
      { fx: bottomStartX + seamAllowanceDraw,                  fy: seamLineY + bottomDrawLength * 0.35,   label: '1/2" SEAM (DBL FOLD)', color: '#0000FF' },
      { fx: bottomStartX + bottomDrawWidth - seamAllowanceDraw, fy: seamLineY + bottomDrawLength * 0.55,  label: '1/2" SEAM (DBL FOLD)', color: '#0000FF' },
      { fx: bottomStartX + bottomDrawWidth,                    fy: bottomSeamY,                           label: 'BOTTOM SEAM (3")', color: '#0000FF' },
      ...seamResult.callouts,
    ];

    const MIN_SLOT = 18;
    let cursor = -Infinity;
    const rightGutter = callouts
      .sort((a, b) => a.fy - b.fy)
      .map(c => {
        const ty = Math.max(c.fy, cursor + MIN_SLOT);
        cursor = ty;
        return this.leaderLine(c.fx, c.fy, textX, ty, c.label, c.color);
      })
      .join('');

    // Architectural dimension lines
    const dimTopWidth  = this.dimLine(topStartX,    patternStartY,  topStartX + topDrawWidth,             patternStartY, -28, `${topSectionWidth}"`);
    const dimTotalLen  = this.dimLine(drawLeftEdge, patternStartY,  drawLeftEdge,                         bottomEdgeY,    55, `${totalLength}"`);
    const dimBottomLen = this.dimLine(drawLeftEdge, seamLineY,      drawLeftEdge,                         bottomEdgeY,    35, `${bottomLength}"`);
    const dimTopHeight = this.dimLine(drawLeftEdge, patternStartY,  drawLeftEdge,                         seamLineY,      20, '8"');
    const dimBotSeam   = this.dimLine(drawLeftEdge, bottomSeamY,    drawLeftEdge,                         bottomEdgeY,    12, '3"');
    const dimBotWidth  = this.dimLine(bottomStartX, bottomEdgeY,    bottomStartX + bottomDrawWidth,        bottomEdgeY,    35, `${bottomSectionWidth}"`);
    const dimSeamWid   = this.dimLine(
      bottomStartX + seamAllowanceDraw, seamLineY,
      bottomStartX + bottomDrawWidth - seamAllowanceDraw, seamLineY,
      14, `${bottomSectionWidth - seamAllowance * 2}"`
    );

    return `
        <div class="section-title" style="text-align: center;">MATERIAL CUT PATTERN</div>
        <div class="pattern-container">
            <svg viewBox="0 0 ${svgWidth} ${svgHeight}" class="pattern-svg"
                 preserveAspectRatio="xMidYMid meet" overflow="hidden">
                ${svgDefs}

                <!-- Two-section rectangles (top narrower, bottom wider) -->
                <g stroke="#000" stroke-width="2" fill="none">
                    <rect x="${topStartX.toFixed(2)}" y="${patternStartY.toFixed(2)}" width="${topDrawWidth.toFixed(2)}" height="${topSectionDrawHeight.toFixed(2)}"/>
                    <rect x="${bottomStartX.toFixed(2)}" y="${seamLineY.toFixed(2)}" width="${bottomDrawWidth.toFixed(2)}" height="${bottomDrawLength.toFixed(2)}"/>
                </g>

                <!-- Construction seam lines (dashed, blue) -->
                <g stroke="#0000FF" stroke-width="1.5" stroke-dasharray="4,3" fill="none">
                    <line x1="${topStartX.toFixed(2)}" y1="${seamLineY.toFixed(2)}" x2="${(topStartX + topDrawWidth).toFixed(2)}" y2="${seamLineY.toFixed(2)}"/>
                    <line x1="${(bottomStartX + seamAllowanceDraw).toFixed(2)}" y1="${seamLineY.toFixed(2)}" x2="${(bottomStartX + seamAllowanceDraw).toFixed(2)}" y2="${(seamLineY + bottomDrawLength).toFixed(2)}"/>
                    <line x1="${(bottomStartX + bottomDrawWidth - seamAllowanceDraw).toFixed(2)}" y1="${seamLineY.toFixed(2)}" x2="${(bottomStartX + bottomDrawWidth - seamAllowanceDraw).toFixed(2)}" y2="${(seamLineY + bottomDrawLength).toFixed(2)}"/>
                    <line x1="${bottomStartX.toFixed(2)}" y1="${bottomSeamY.toFixed(2)}" x2="${(bottomStartX + bottomDrawWidth).toFixed(2)}" y2="${bottomSeamY.toFixed(2)}"/>
                </g>

                <!-- Fabric piece join seams (red dashed) -->
                ${seamResult.lines}

                <!-- Name plate -->
                <g stroke="#000" stroke-width="1" fill="none">
                    <rect x="${namePlateLeftX.toFixed(2)}" y="${namePlateY.toFixed(2)}" width="${namePlateWidth.toFixed(2)}" height="${namePlateHeight.toFixed(2)}"/>
                    <circle cx="${(namePlateLeftX + 3).toFixed(2)}" cy="${(namePlateY + 3).toFixed(2)}" r="1" fill="#000"/>
                    <circle cx="${(namePlateRightX - 3).toFixed(2)}" cy="${(namePlateY + 3).toFixed(2)}" r="1" fill="#000"/>
                    <circle cx="${(namePlateLeftX + 3).toFixed(2)}" cy="${(namePlateY + namePlateHeight - 3).toFixed(2)}" r="1" fill="#000"/>
                    <circle cx="${(namePlateRightX - 3).toFixed(2)}" cy="${(namePlateY + namePlateHeight - 3).toFixed(2)}" r="1" fill="#000"/>
                </g>

                ${pulleyElements}

                <!-- Architectural dimension lines -->
                ${dimTopWidth}
                ${dimTotalLen}
                ${dimBottomLen}
                ${dimTopHeight}
                ${dimBotSeam}
                ${dimBotWidth}
                ${dimSeamWid}

                <!-- Right-gutter leader callouts -->
                ${rightGutter}

            </svg>
        </div>`;
  }

  /**
   * Generate fabric cut rows based on actual pieces needed
   */
  private static generateFabricCutRows(orderData: any): string {
    const topSectionLength = 8;
    const bottomSectionLength = orderData.drop + 3;
    const totalLength = topSectionLength + bottomSectionLength;
    const maxWidth = orderData.width + 2;  // 1" seam allowance per side (double fold)

    // Check if multiple pieces needed (fabric roll is 46" wide, usable length per piece is ~44")
    const needsMultiplePieces = (maxWidth > 44 && totalLength > 44);

    if (!needsMultiplePieces) {
      // Single piece of fabric
      return `
                    <tr>
                        <td>Fabric</td>
                        <td>${maxWidth}" W × ${totalLength}" L</td>
                    </tr>`;
    } else {
      // Multiple pieces needed - calculate how many
      const fabricPieces = Math.ceil(totalLength / 44);
      const seamAllowancePerPiece = 0.5; // 1/2" seam allowance per piece
      let rows = '';

      for (let i = 1; i <= fabricPieces; i++) {
        const basePieceLength = i < fabricPieces ? 44 : (totalLength - (44 * (fabricPieces - 1)));
        // Add seam allowance to each piece
        const pieceLength = basePieceLength + seamAllowancePerPiece;
        rows += `
                    <tr>
                        <td>Fabric - Piece ${i}</td>
                        <td>${maxWidth}" W × ${pieceLength.toFixed(1)}" L (includes 0.5" seam allowance)</td>
                    </tr>`;
      }

      return rows;
    }
  }

  /**
   * Architectural linear dimension: witness lines + arrow-terminated dim line + centred label.
   * ax,ay / bx,by — anchor points ON the object.
   * off — perpendicular offset (positive = left of A→B direction).
   */
  private static dimLine(
    ax: number, ay: number,
    bx: number, by: number,
    off: number,
    label: string
  ): string {
    const dx = bx - ax, dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len, py = dx / len;   // unit perpendicular (left of A→B)
    const sgn = off >= 0 ? 1 : -1;
    const gap = 3, over = 4;

    const ax2 = ax + px * off,  ay2 = ay + py * off;
    const bx2 = bx + px * off,  by2 = by + py * off;

    const w1x1 = ax + px * sgn * gap,          w1y1 = ay + py * sgn * gap;
    const w1x2 = ax + px * (off + sgn * over), w1y2 = ay + py * (off + sgn * over);
    const w2x1 = bx + px * sgn * gap,          w2y1 = by + py * sgn * gap;
    const w2x2 = bx + px * (off + sgn * over), w2y2 = by + py * (off + sgn * over);

    const mx = (ax2 + bx2) / 2, my = (ay2 + by2) / 2;
    let ang = Math.atan2(dy, dx) * 180 / Math.PI;
    if (ang > 90 || ang < -90) ang += 180;

    return `
    <g class="dim-group">
      <line class="dim-witness" x1="${w1x1.toFixed(2)}" y1="${w1y1.toFixed(2)}" x2="${w1x2.toFixed(2)}" y2="${w1y2.toFixed(2)}"/>
      <line class="dim-witness" x1="${w2x1.toFixed(2)}" y1="${w2y1.toFixed(2)}" x2="${w2x2.toFixed(2)}" y2="${w2y2.toFixed(2)}"/>
      <line class="dim-line" x1="${ax2.toFixed(2)}" y1="${ay2.toFixed(2)}" x2="${bx2.toFixed(2)}" y2="${by2.toFixed(2)}"
            marker-start="url(#dimArrow)" marker-end="url(#dimArrow)"/>
      <text class="dim-text" x="${mx.toFixed(2)}" y="${my.toFixed(2)}" text-anchor="middle" dy="-3"
            transform="rotate(${ang.toFixed(2)},${mx.toFixed(2)},${my.toFixed(2)})">${label}</text>
    </g>`;
  }

  /**
   * Leader callout: orange dot on feature point, line + label out in gutter.
   */
  private static leaderLine(
    fx: number, fy: number,
    tx: number, ty: number,
    label: string,
    color: string = '#FF6600'
  ): string {
    const anchor = tx >= fx ? 'start' : 'end';
    const pad = tx >= fx ? 4 : -4;
    return `
    <g class="leader-group">
      <line class="leader-line" x1="${fx.toFixed(2)}" y1="${fy.toFixed(2)}" x2="${tx.toFixed(2)}" y2="${ty.toFixed(2)}"
            stroke="${color}" marker-start="url(#leadDot)"/>
      <text class="leader-text" x="${(tx + pad).toFixed(2)}" y="${ty.toFixed(2)}" fill="${color}"
            text-anchor="${anchor}" dominant-baseline="middle">${label}</text>
    </g>`;
  }

  /**
   * Generate fabric join seam lines (red dashed geometry only).
   * Returns SVG line markup + callout data for the right-gutter allocator.
   */
  private static generateSeamLines(
    width: number,
    topStartX: number,
    topDrawWidth: number,
    bottomStartX: number,
    bottomDrawWidth: number,
    startY: number,
    topDrawHeight: number,
    bottomStartY: number,
    bottomDrawLength: number,
    scale: number
  ): { lines: string, callouts: Array<{fx: number, fy: number, label: string, color: string}> } {
    const topSectionLength = 8;
    const bottomSectionLength = bottomDrawLength / scale;
    const totalLength = topSectionLength + bottomSectionLength;

    const fabricPieces = (width > 44 && totalLength > 44) ? Math.ceil(totalLength / 44) : 1;

    if (fabricPieces <= 1) {
      return { lines: '', callouts: [] };
    }

    let seamLines = '<g stroke="#CC0000" stroke-width="1" stroke-dasharray="3,3" fill="none">';
    const callouts: Array<{fx: number, fy: number, label: string, color: string}> = [];

    for (let i = 1; i < fabricPieces; i++) {
      const seamLengthPosition = i * 44;

      if (seamLengthPosition <= topSectionLength) {
        const seamY = startY + (seamLengthPosition * scale);
        seamLines += `
          <line x1="${topStartX.toFixed(2)}" y1="${seamY.toFixed(2)}" x2="${(topStartX + topDrawWidth).toFixed(2)}" y2="${seamY.toFixed(2)}"/>`;
        callouts.push({ fx: topStartX + topDrawWidth, fy: seamY, label: 'JOIN +0.5" allow', color: '#CC0000' });
      } else {
        const seamLengthInBottom = seamLengthPosition - topSectionLength;
        const seamY = bottomStartY + (seamLengthInBottom * scale);
        if (seamY < bottomStartY + bottomDrawLength) {
          seamLines += `
            <line x1="${bottomStartX.toFixed(2)}" y1="${seamY.toFixed(2)}" x2="${(bottomStartX + bottomDrawWidth).toFixed(2)}" y2="${seamY.toFixed(2)}"/>`;
          callouts.push({ fx: bottomStartX + bottomDrawWidth, fy: seamY, label: 'JOIN +0.5" allow', color: '#CC0000' });
        }
      }
    }

    seamLines += '</g>';
    return { lines: seamLines, callouts };
  }

  /**
   * Generate bill of materials page
   */
  private static generateBillOfMaterialsHTML(orderData: any, costs: CalculationResult, needsPulleys: boolean): string {
    const rollPulleyCount = orderData.width > 48 ? 3 : 2;
    const pullCord1Length = Math.round(orderData.drop * 3.25);
    const pulleySpacing = orderData.width > 12 ? Math.round((orderData.width - 6) / (rollPulleyCount - 1)) : 6;

    // Calculate total stainless wood screws needed
    const screwMount = 4;
    const screwPulley = needsPulleys ? rollPulleyCount * 2 : 0;
    const screwRetainer = needsPulleys ? 1 : 0;
    const screwCleat = needsPulleys ? 2 : 0;
    const screwTotal = screwMount + screwPulley + screwRetainer + screwCleat;

    // Get correct fabric inventory ID based on material and color
    const fabricId = this.getFabricInventoryId(orderData.material, orderData.colour);

    const materials: string[][] = [
      [fabricId, `${escapeHtml(orderData.materialName)} Fabric - ${escapeHtml(orderData.colourName)}`, `${costs.fabricYardage}`, 'yards', '', '', '✓'],
      ['2x2-TP', '2x2 - Treated Pine', '1', 'pc', '', '', '✓'],
      ['SCH40', 'Sch40 Pipe', '1', 'pc', '', '', '✓'],
      ['MNT-BOX', 'Mounting Box', '2', 'pcs', '', '', '✓'],
      ['SET-SCR', 'Mounting Box Set Screws - M2 x 16mm', '8', 'pcs', '', '', '✓'],
      ['NAME-PLT', 'Made.bb Name Plate', '1', 'pc', '', '', '✓'],
      ['PLT-SCR', 'Made.bb Name Plate Screws - Self Tapping 1"', '4', 'pcs', '', '', '✓'],
      // Consolidated stainless wood screws with subsections
      ['SS-SCR', `2" Stainless Wood Screw`, screwTotal.toString(), 'pcs', '', '', ''],
      ['', `  └─ Mount (Pack)`, screwMount.toString(), 'pcs', '', '✓', ''],
    ];

    if (needsPulleys) {
      materials.push(
        ['', `  └─ Pulley (Assemble)`, screwPulley.toString(), 'pcs', '', '', '✓'],
        ['', `  └─ Retainer (Pack)`, screwRetainer.toString(), 'pc', '', '✓', ''],
        ['', `  └─ Cleat (Pack)`, screwCleat.toString(), 'pcs', '', '✓', '']
      );
    }

    if (needsPulleys) {
      materials.push(
        ['RP-BOX', 'Roll Pulley Box', rollPulleyCount.toString(), 'pcs', '', '', '✓'],
        ['RP-WHL', 'Roll Pulley Wheel', rollPulleyCount.toString(), 'pcs', '', '', '✓'],
        ['RP-CVR', 'Roll Pulley Cover', rollPulleyCount.toString(), 'pcs', '', '', '✓'],
        ['RP-CAP', 'M3x10mm Capscrew', (rollPulleyCount * 2).toString(), 'pcs', '', '', '✓']
      );

      // Add pull cords with calculated lengths
      materials.push(['PC-1', `Pull Cord 1 - ${pullCord1Length}"`, '1', 'pc', '', '', '✓']);

      if (rollPulleyCount >= 2) {
        const pullCord2Length = pullCord1Length + pulleySpacing;
        materials.push(['PC-2', `Pull Cord 2 - ${pullCord2Length}"`, '1', 'pc', '', '', '✓']);
      }

      if (rollPulleyCount === 3) {
        const pullCord3Length = pullCord1Length + (pulleySpacing * 2);
        materials.push(['PC-3', `Pull Cord 3 - ${pullCord3Length}"`, '1', 'pc', '', '', '✓']);
      }

      // Add pull cord hardware (screws now consolidated above)
      materials.push(
        ['PC-END', 'Pull Cord End', '1', 'pc', '', '', '✓'],
        ['PC-RET', 'Pull Cord End Wall Retainer', '1', 'pc', '', '✓', ''],
        ['PC-CLT', 'Pull Cord Wall Cleat', '1', 'pc', '', '✓', ''],
        ['PC-ANC', 'Pull Cord Anchor', rollPulleyCount.toString(), 'pcs', '', '', '✓'],
        ['PC-SCR3', '1 1/2" Wood Screw (End)', '1', 'pc', '', '', '✓']
      );
    }

    materials.push(
      ['HW-BAG', 'Hardware Bag', '1', 'pc', '', '✓', ''],
      ['BOX', 'Shipping Box', '1', 'pc', '', '', '✓']
    );

    const materialRows = materials.map(([item, desc, qty, unit, picked, pack, assembly]) => {
        // Check if this is a subsection row (empty item code)
        const isSubsection = item === '';
        const rowClass = isSubsection ? ' class="subsection-row"' : '';
        return `
        <tr${rowClass}>
            <td>${item}</td>
            <td>${desc}</td>
            <td>${qty}</td>
            <td>${unit}</td>
            <td>${picked}</td>
            <td style="text-align: center;">${pack}</td>
            <td style="text-align: center;">${assembly}</td>
        </tr>
    `;
    }).join('');

    // Generate assembly notes
    let assemblyNotes = [
      'Operator initials "Picked" column when item is collected',
      'Check "Pack" for items going to packaging',
      'Check "Assembly" for items going to assembly station'
    ];

    if (needsPulleys) {
      const widthFeet = Math.round(orderData.width / 12 * 10) / 10;
      const pulleyText = rollPulleyCount === 2 ?
        `NOTE: This shade (${widthFeet}') requires 2 roll pulley assemblies` :
        `NOTE: This shade (${widthFeet}') requires 3 roll pulley assemblies (>4' width)`;
      assemblyNotes.push(pulleyText);
      assemblyNotes.push(`Pull cord lengths calculated: PC-1=${pullCord1Length}", others vary by spacing`);
    }

    const assemblyNotesList = assemblyNotes.map(note =>
      note.startsWith('NOTE:') ? `<li class="highlight">${note}</li>` : `<li>${note}</li>`
    ).join('');

    return `
<div class="page">
    <div class="header">
        <h1>Bill of Materials / Pick Sheet</h1>
        <p>Order: ${orderData.orderId}</p>
    </div>

    <table class="table">
        <thead>
            <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Picked</th>
                <th>Pack</th>
                <th>Assembly</th>
            </tr>
        </thead>
        <tbody>
            ${materialRows}
        </tbody>
    </table>

    <div style="display: flex; gap: 20px;">
        <div class="notes" style="flex: 1;">
            <div class="section-title">ASSEMBLY NOTES:</div>
            <ul>
                ${assemblyNotesList}
            </ul>
        </div>

        <div class="qa-box" style="flex: 0 0 300px;">
            <div class="section-title">QUALITY ASSURANCE INSPECTION:</div>
            <table class="table" style="font-size: 10px;">
                <thead>
                    <tr>
                        <th style="width: 60%;">Check Item</th>
                        <th style="width: 20%;">Pass</th>
                        <th style="width: 20%;">Fail</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Width Match</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                    <tr>
                        <td>Drop Match</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                    <tr>
                        <td>Unit Colour Verified</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                    <tr>
                        <td>Hardware Colour Verified</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                    <tr>
                        <td>All Components Included</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                    <tr>
                        <td>Final Assembly Quality</td>
                        <td style="text-align: center;">☐</td>
                        <td style="text-align: center;">☐</td>
                    </tr>
                </tbody>
            </table>
            <div style="margin-top: 15px; font-size: 10px;">
                <strong>Inspector Signature:</strong> ________________________
                <br><br>
                <strong>Date:</strong> ________________ <strong>Time:</strong> __________
            </div>
        </div>
    </div>
</div>`;
  }

  /**
   * Capitalize first letter
   */
  private static capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
